import type { ReactNode } from 'react'
import { forwardRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

import { getTextFieldStyles } from '@nxt-insurance/ui-kit/components/formComponents/TextFieldContainer/styles'
import { bgColor, borderColor } from '@nxt-insurance/ui-kit/styles/colors'

import * as RadixSelect from '@radix-ui/react-select'
import type { Color } from '@ui-kit/types'

type Props = RadixSelect.SelectProps & {
  triggerIcon: JSX.Element
  label?: string
  children: ReactNode
  id?: string
  backgroundColor?: Color
  borderColor?: Color
  width?: number
}

export const Select = (props: Props) => {
  const {
    backgroundColor,
    borderColor: borderColorProp,
    width,
    id,
    label,
    triggerIcon,
    children,
    ...rootProps
  } = props
  const [isFocused, setIsFocused] = useState(false)
  const triggerBg = bgColor({ color: backgroundColor ?? 'transparent' })
  const triggerBorderColor = borderColor({ color: borderColorProp })
  const { container, fieldContainer, childrenContainer } = getTextFieldStyles({
    borderColor: triggerBorderColor,
    bgColor: triggerBg,
  })({
    disabled: props.disabled,
    isFocused,
    isFilled: true,
    styleType: 'outlined',
    hasLabel: false,
  })

  return (
    <RadixSelect.Root onOpenChange={setIsFocused} {...rootProps}>
      <RadixSelect.Trigger
        className={twMerge('group flex w-full items-center', container())}
        aria-label={label}
        id={id}
      >
        <span
          className={fieldContainer({
            className: 'w-full',
          })}
        >
          <span
            className={childrenContainer({
              className: 'flex w-full flex-row items-center',
            })}
          >
            <span className="flex-1">
              <RadixSelect.Value />
            </span>
            <RadixSelect.Icon className="transition-all duration-300 group-aria-expanded:rotate-180">
              {triggerIcon}
            </RadixSelect.Icon>
          </span>
        </span>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className="z-50 max-h-[210px] w-full overflow-auto rounded-b-lg border border-ui-grey-500 bg-ui-light shadow-lg"
          style={{ width }}
          position="popper"
        >
          <RadixSelect.Viewport>{children}</RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  )
}

export const SelectItem = forwardRef<
  HTMLDivElement,
  RadixSelect.SelectItemProps & { children: ReactNode; width?: number }
>(({ children, ...props }, forwardedRef) => {
  return (
    <RadixSelect.Item
      className="px-4 py-2.5 transition duration-300 data-[highlighted]:bg-ui-grey-50 data-[state=checked]:bg-ui-grey-50"
      {...props}
      ref={forwardedRef}
    >
      <RadixSelect.ItemText className="flex flex-row items-center">{children}</RadixSelect.ItemText>
    </RadixSelect.Item>
  )
})
